import { type RefObject, useEffect } from "react";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type UseFocusTrapOptions = {
  /** Return focus to the element that was focused when the trap activated. Defaults to `true`. */
  restoreFocus?: boolean;
  /** Where focus goes when the trap activates. Defaults to `"first"`. */
  initialFocus?: "first" | "none";
};

const getFocusable = (container: HTMLElement) =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

/**
 * Keeps keyboard focus inside `containerRef` while `active`: Tab and
 * Shift+Tab wrap between the first and last focusable descendants, and focus
 * that has escaped the container is pulled back in on the next Tab. When the
 * container has no focusable descendants, focus stays on the container itself
 * (give it `tabIndex={-1}`).
 */
export const useFocusTrap = <T extends HTMLElement>(
  containerRef: RefObject<T | null>,
  active: boolean,
  { restoreFocus = true, initialFocus = "first" }: UseFocusTrapOptions = {},
) => {
  useEffect(() => {
    if (!active) return;

    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    if (initialFocus === "first") {
      const [first] = getFocusable(container);
      (first ?? container).focus({ preventScroll: true });
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const focusable = getFocusable(container);
      if (focusable.length === 0) {
        e.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      const isOutside = !container.contains(current);

      if (e.shiftKey && (current === first || isOutside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (current === last || isOutside)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (restoreFocus && previouslyFocused?.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [active, containerRef, restoreFocus, initialFocus]);
};
